import React, { useState } from 'react';
import { Box, TextField } from '@mui/material';
import clsx from 'clsx';
import { Controller } from 'react-hook-form';
import { CalendarIcon } from 'tt-ui-lib/icons';
import DeleteModal from '../../DeleteModal';
import styles from '../../ProfBackgroundItemEditing.module.scss';
import moment from 'moment';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormHelperText from '@mui/material/FormHelperText';

const openPickerSlots = {
  openPickerIcon: () => <CalendarIcon style={{ width: 26, height: 26, cursor: 'pointer' }} />,
};

const EducationItemEditing = ({
  profProps,
  index,
  formName,
  control,
  errors,
  isNew,
  handleDelete,
  setValue,
  setError,
  experienceName,
}) => {
  const [isPresent, setIsPresent] = useState(!profProps?.end_date);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const onDelete = async () => {
    try {
      setIsDeleteLoading(true);
      const res = await handleDelete(index);
      if (res) {
        setIsDeleteModalOpen(false);
        setIsDeleteLoading(false);
      }
    } catch (e) {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className={styles.profBackgroundItemWrapper}>
      <div className={clsx(styles.profItemIndex, isNew ? styles.hidden : '')}>
        {`${index + 1}.`}
      </div>
      <div className={styles.profItemSecondWrapper}>
        <div className={styles.firstLineWrapper}>
          <Controller
            name={`${formName}[${index}].title`}
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <TextField
                className={styles.editInput}
                label="Title of position"
                value={value}
                onChange={onChange}
                error={!!errors[formName]?.[index]?.title}
                helperText={errors[formName]?.[index]?.title?.message}
              />
            )}
          />
          <Controller
            name={`${formName}[${index}].company_name`}
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <TextField
                className={styles.editInput}
                label="Company name"
                value={value}
                onChange={onChange}
                error={!!errors[formName]?.[index]?.company_name}
                helperText={errors[formName]?.[index]?.company_name?.message}
              />
            )}
          />
        </div>
        <div className={styles.datesWrapper}>
          <Box className={styles.datePicker}>
            <Controller
              name={`${formName}[${index}].start_date`}
              control={control}
              render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="Start date"
                    views={['year', 'month']}
                    inputFormat="YYYY/MM"
                    minDate={1970}
                    disableFuture
                    value={value ? new Date(value) : null}
                    onChange={onChange}
                    className={styles.datePickerInput}
                    slots={openPickerSlots}
                    slotProps={{
                      textField: () => ({
                        error: !!errors[formName]?.[index]?.start_date?.message,
                        readOnly: true,
                      }),
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            {!!errors[formName]?.[index]?.start_date && (
              <FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px 0' }}>
                {errors[formName]?.[index]?.start_date?.message}
              </FormHelperText>
            )}
          </Box>
          <Box className={styles.datePicker}>
            <Controller
              name={`${formName}[${index}].end_date`}
              control={control}
              render={({ field: { value, onChange }, fieldState: { invalid } }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DesktopDatePicker
                    label="End date"
                    views={['year', 'month']}
                    inputFormat="YYYY/MM"
                    minDate={1970}
                    disableFuture
                    value={value ? new Date(value) : null}
                    onChange={onChange}
                    className={styles.datePickerInput}
                    slots={openPickerSlots}
                    slotProps={{
                      textField: () => ({
                        error: !!errors[formName]?.[index]?.end_date?.message,
                        readOnly: true,
                      }),
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            {!!errors[formName]?.[index]?.end_date && (
              <FormHelperText sx={{ color: '#d32f2f', margin: '3px 14px 0' }}>
                {errors[formName]?.[index]?.end_date?.message}
              </FormHelperText>
            )}
          </Box>
          <div className={styles.checkboxWrapper}>
            <Controller
              name={`${formName}[${index}].is_present`}
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <input
                  {...field}
                  className={styles.checkbox}
                  id={`checkbox-present-${formName}[${index}].is_present`}
                  type="checkbox"
                  value={value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setValue(`${formName}[${index}].end_date`, null);
                      setError(`${formName}[${index}].end_date`, null);
                    } else {
                      const date = moment(new Date()).format('YYYY-MM-DD');
                      setValue(`${formName}[${index}].end_date`, date);
                    }
                    onChange(e);
                    setIsPresent(e.target.checked);
                  }}
                  defaultChecked={isPresent}
                />
              )}
            />
            <label htmlFor={`checkbox-present-${formName}[${index}].is_present`}>Present</label>
          </div>
          <button
            className={styles.deleteIconWrapper}
            type="button"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            <img
              src="/images/templates/delete-icon-red.png"
              alt=""
              style={{ width: 24, height: 24 }}
            />
          </button>
        </div>
      </div>
      <button
        className={styles.deleteIconWrapper}
        type="button"
        onClick={() => setIsDeleteModalOpen(true)}
      >
        <img src="/images/templates/delete-icon-red.png" alt="" style={{ width: 24, height: 24 }} />
      </button>
      <DeleteModal
        onDelete={onDelete}
        isModelOpen={isDeleteModalOpen}
        isDeleteLoading={isDeleteLoading}
        setIsOpen={setIsDeleteModalOpen}
        modalText={
          experienceName
            ? `Do you really want to delete position ${experienceName}?`
            : `Do you really want to delete this item?`
        }
      />
    </div>
  );
};

export default EducationItemEditing;
